import React, { useMemo } from 'react';
import loadable, { ExtraComponentProps } from '@loadable/component';
import useConfig from 'AppShell/hooks/useConfig';
import { useFooterSocialMedia } from 'OneWestwingFooter/hooks/useFooterSocialMedia';
import { AVAILABLE_COUNTRY_CODES } from 'AppShell/appshell_types';

const IDeal = loadable(() => import('@westwing/ui-kit/PaymentMethod/IdealLogoIcon'));
const AmericanExpress = loadable(() => import('@westwing/ui-kit/PaymentMethod/Amex'));
const MasterCard = loadable(() => import('@westwing/ui-kit/PaymentMethod/MasterCardLogoIcon'));
const Paypal = loadable(() => import('@westwing/ui-kit/PaymentMethod/PaypalLogoIcon'));
const Rechnung = loadable(() => import('@westwing/ui-kit/PaymentMethod/WIPLogoIcon'));
const Sepa = loadable(() => import('@westwing/ui-kit/PaymentMethod/SepaV2'));
const Visa = loadable(() => import('@westwing/ui-kit/PaymentMethod/VisaLogoIcon'));
const Przelewy = loadable(() => import('@westwing/ui-kit/PaymentMethod/Przelewy24LogoIcon'));
const Blik = loadable(() => import('@westwing/ui-kit/PaymentMethod/BlikLogoIcon'));
const CashOnDelivery = loadable(() => import('@westwing/ui-kit/PaymentMethod/CashOnDeliveryLogoIcon'));
const Bancontact = loadable(() => import('@westwing/ui-kit/PaymentMethod/BancontactLogoIcon'));
const Payconiq = loadable(() => import('@westwing/ui-kit/PaymentMethod/BancontactPayconiqLogoIcon'));
const Klarna = loadable(() => import('@westwing/ui-kit/PaymentMethod/Klarna'));
const Aplazame = loadable(() => import('@westwing/ui-kit/PaymentMethod/AplazameLogoIcon'));
const Sofort = loadable(() => import('@westwing/ui-kit/PaymentMethod/SofortLogoIcon'));
const PayU = loadable(() => import('@westwing/ui-kit/PaymentMethod/PayULogoIcon'));

const Budbee = loadable(() => import('@westwing/ui-kit/DeliveryPartners/Budbee'));
const DiePost = loadable(() => import('@westwing/ui-kit/DeliveryPartners/DiePost'));
const Dpd = loadable(() => import('@westwing/ui-kit/DeliveryPartners/Dpd'));
const Hermes = loadable(() => import('@westwing/ui-kit/DeliveryPartners/Hermes'));
const Italmondo = loadable(() => import('@westwing/ui-kit/DeliveryPartners/Italmondo'));
const PostNL = loadable(() => import('@westwing/ui-kit/DeliveryPartners/PostNL'));
const Relais = loadable(() => import('@westwing/ui-kit/DeliveryPartners/Relais'));
const Rhenus = loadable(() => import('@westwing/ui-kit/DeliveryPartners/Rhenus'));
const Sda = loadable(() => import('@westwing/ui-kit/DeliveryPartners/Sda'));
const Seur = loadable(() => import('@westwing/ui-kit/DeliveryPartners/Seur'));
const Tamdis = loadable(() => import('@westwing/ui-kit/DeliveryPartners/Tamdis'));
const Ups = loadable(() => import('@westwing/ui-kit/DeliveryPartners/Ups'));
const Vir = loadable(() => import('@westwing/ui-kit/DeliveryPartners/Vir'));
const Dhl = loadable(() => import('@westwing/ui-kit/DeliveryPartners/Dhl'));
const WWDeliveryService = loadable(() => import('@westwing/ui-kit/DeliveryPartners/WWDeliveryService'));
const BPost = loadable(() => import('@westwing/ui-kit/DeliveryPartners/BPost'));

export enum Platforms {
    INSTAGRAM = 'Instagram',
    FACEBOOK = 'Facebook',
    TIKTOK = 'TikTok',
    PINTEREST = 'Pinterest',
    YOUTUBE = 'Youtube',
}

export type SvgComponentType = React.ComponentType<React.SVGProps<SVGSVGElement> & ExtraComponentProps>;

type FooterCountryData = {
    [key: string]: {
        paymentProviders: (SvgComponentType | (() => React.Component))[];
        deliveryPartners: (SvgComponentType | (() => React.Component))[];
    };
};

export const Vorkasse = loadable(
    (props: { componentName: string }) => import(`@westwing/ui-kit/PaymentMethod/${props.componentName}/index.js`)
);

export const VorkasseLogo = () => {
    const { currentCountry } = useConfig();
    // we need to define component name here, as loadable func doesn't allow to do any logic calculations inside
    const componentName = `Vorkasse${currentCountry.toUpperCase()}LogoIcon`;
    return <Vorkasse componentName={componentName} width={48} height={32} />;
};

const paymentProviderPerAppMap: Record<
    'club' | 'shop',
    Record<(typeof AVAILABLE_COUNTRY_CODES)[number], (SvgComponentType | (() => React.Component))[]>
> = {
    club: {
        de: [Paypal, Sepa, MasterCard, Visa, AmericanExpress, Sofort],
        ch: [Paypal, Visa, MasterCard, AmericanExpress],
        nl: [Paypal, MasterCard, Visa, AmericanExpress, IDeal, Bancontact, Payconiq],
        pl: [Paypal, MasterCard, Visa, AmericanExpress, Przelewy, Blik, CashOnDelivery],
        fr: [Paypal, MasterCard, Visa, AmericanExpress, Bancontact, Payconiq],
        es: [Paypal, MasterCard, Visa, AmericanExpress, CashOnDelivery, Aplazame],
        it: [Paypal, MasterCard, Visa, AmericanExpress, Sofort, VorkasseLogo, CashOnDelivery],
        sk: [Paypal, MasterCard, Visa, AmericanExpress, VorkasseLogo, CashOnDelivery],
        cz: [Paypal, MasterCard, Visa, AmericanExpress, PayU, VorkasseLogo, CashOnDelivery],
        pt: [Paypal, MasterCard, Visa, AmericanExpress, PayU, VorkasseLogo, CashOnDelivery],
    },
    shop: {
        de: [Paypal, MasterCard, Visa, AmericanExpress, Klarna, Sepa, VorkasseLogo, Rechnung],
        ch: [Paypal, MasterCard, Visa, AmericanExpress, Klarna, VorkasseLogo],
        nl: [Paypal, MasterCard, Visa, AmericanExpress, Klarna, IDeal, Bancontact, Payconiq, VorkasseLogo],
        pl: [Paypal, MasterCard, Visa, AmericanExpress, Klarna, Przelewy, Blik, VorkasseLogo, CashOnDelivery],
        fr: [Paypal, MasterCard, Visa, AmericanExpress, Klarna, Bancontact, Payconiq, VorkasseLogo],
        es: [Paypal, MasterCard, Visa, AmericanExpress, Klarna, VorkasseLogo, CashOnDelivery],
        it: [Paypal, MasterCard, Visa, AmericanExpress, Klarna, VorkasseLogo, CashOnDelivery],
        sk: [Paypal, MasterCard, Visa, AmericanExpress, VorkasseLogo, CashOnDelivery],
        cz: [Paypal, MasterCard, Visa, AmericanExpress, Klarna, VorkasseLogo, CashOnDelivery],
        pt: [Paypal, MasterCard, Visa, AmericanExpress, Klarna, VorkasseLogo, CashOnDelivery],
    },
};

export const useFooterPartnersInfo = (isClub: boolean) => {
    const { currentCountry } = useConfig();

    const footerCountryData: FooterCountryData = useMemo(() => {
        const paymentProviderPerCountryMap = isClub ? paymentProviderPerAppMap.club : paymentProviderPerAppMap.shop;
        return {
            de: {
                paymentProviders: paymentProviderPerCountryMap.de,
                deliveryPartners: [Dhl, Hermes, Rhenus, WWDeliveryService],
            },
            ch: {
                paymentProviders: paymentProviderPerCountryMap.ch,
                deliveryPartners: [DiePost],
            },
            nl: {
                paymentProviders: paymentProviderPerCountryMap.nl,
                deliveryPartners: [PostNL, Budbee, BPost],
            },
            pl: {
                paymentProviders: paymentProviderPerCountryMap.pl,
                deliveryPartners: [Dhl, Dpd, Rhenus],
            },
            fr: {
                paymentProviders: paymentProviderPerCountryMap.fr,
                deliveryPartners: [Dpd, Dhl, Relais, Vir, BPost],
            },
            es: {
                paymentProviders: paymentProviderPerCountryMap.es,
                deliveryPartners: [Seur, Tamdis],
            },
            it: {
                paymentProviders: paymentProviderPerCountryMap.it,
                deliveryPartners: [Sda, Ups, Italmondo],
            },
            sk: {
                paymentProviders: paymentProviderPerCountryMap.sk,
                deliveryPartners: [Dhl, Dpd, Rhenus],
            },
            cz: {
                paymentProviders: paymentProviderPerCountryMap.cz,
                deliveryPartners: [Dhl, Dpd, Rhenus],
            },
            pt: {
                paymentProviders: paymentProviderPerCountryMap.pt,
                deliveryPartners: [Dhl, Dpd, Rhenus],
            },
        };
    }, [isClub]);

    const socialMedia = useFooterSocialMedia();

    return { ...footerCountryData[currentCountry ?? 'de'], socialMedia };
};
